import React, { useState, useEffect } from 'react';
import VideoPlayer from '../../../components/VideoPlayer';
import {
  getVideoOrientation,
  getVideoThumbnail,
  detectOrientationFromThumbnail
} from '../../../shared/videoUtils';
import { LuX } from 'react-icons/lu';
import { FaFacebook, FaPlay } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/firebase';
import { extractString, extractOrder } from '../../../shared/fieldUtils';
import churchThumb from '../../../assets/cathedral_aerial_hero.jpg';

const DEFAULT_MOMENTS = [
  {
    id: 'default-1',
    title: 'Diocesan Holy Mass & Prayer',
    url: churchThumb,
    videoLink: 'https://www.facebook.com/CSIMalabarDiocese/videos/10158473921025501/',
    type: 'video'
  },
  {
    id: 'default-2',
    title: 'Diocesan Choir Ministry',
    url: churchThumb,
    videoLink: 'https://www.facebook.com/CSIMalabarDiocese/videos/10158473921025502/',
    type: 'video'
  },
  {
    id: 'default-3',
    title: 'Parish Youth Convention',
    url: churchThumb,
    videoLink: 'https://www.facebook.com/CSIMalabarDiocese/videos/10158473921025503/',
    type: 'video'
  }
];


function getMediaImage(d) {
  if (!d) return '';
  const candidate =
    d.thumbnail ||
    d.thumb ||
    d.img_url ||
    d.image_url ||
    d.image ||
    d.photo ||
    d.img ||
    d.pic ||
    d.cover ||
    d.poster ||
    d.imageUrl ||
    d.bgImg ||
    d.bg_img ||
    d.picture ||
    d.banner ||
    (typeof d.url === 'string' && !d.url.includes('facebook.com') && !d.url.includes('fb.watch') && !d.url.includes('youtube.com') && !d.url.includes('youtu.be') ? d.url : '');
  return extractString(candidate);
}

function getMediaLink(d) {
  if (!d) return '';
  return extractString(
    d.link ||
    d.video_url ||
    d.fb_url ||
    d.facebook_link ||
    (typeof d.url === 'string' && (d.url.includes('facebook.com') || d.url.includes('fb.watch') || d.url.includes('youtube.com') || d.url.includes('youtu.be')) ? d.url : '')
  );
}

function getTimestampMs(item) {
  if (!item) return 0;
  const t = item.updatedAt || item.createdAt || item.timestamp || item.time_stamp || item.date;
  if (!t) return 0;
  if (typeof t.toDate === 'function') return t.toDate().getTime();
  if (t.seconds) return t.seconds * 1000;
  if (typeof t === 'number') return t;
  if (typeof t === 'string') {
    const d = new Date(t);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }
  return 0;
}

export default function MediaRow({ onSelectSection }) {
  const [liveInfo, setLiveInfo] = useState({
    title: "Holy Mass",
    schedule: "Every Sunday",
    time: "6:00 AM",
    isLive: true,
    bgImg: ""
  });

  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [modalOrientation, setModalOrientation] = useState(null);

  useEffect(() => {
    let isCancelled = false;
    if (selectedVideo) {
      const videoUrl = selectedVideo.videoLink || selectedVideo.link || (typeof selectedVideo.url === 'string' && (selectedVideo.url.includes('facebook.com') || selectedVideo.url.includes('youtube.com')) ? selectedVideo.url : '');
      const initial = getVideoOrientation(selectedVideo);
      setModalOrientation(initial === 'portrait' ? 'portrait' : 'landscape');

      if (!videoUrl) {
        const thumbUrl = selectedVideo.url || selectedVideo.thumbnail;
        if (thumbUrl && typeof thumbUrl === 'string') {
          detectOrientationFromThumbnail(thumbUrl).then((detected) => {
            if (!isCancelled && detected) {
              setModalOrientation(detected === 'portrait' ? 'portrait' : 'landscape');
            }
          });
        }
      }
    } else {
      setModalOrientation(null);
    }
    return () => { isCancelled = true; };
  }, [selectedVideo]);

  useEffect(() => {
    let isMounted = true;

    async function fetchMedia() {
      try {
        // 1. Fetch Livestream config from Firestore across all possible collection names
        const liveCollections = ['youtube', 'youtubes', 'youtube-video', 'youtube_video', 'livestream', 'live-stream', 'live_stream', 'media'];
        let liveSnap = null;

        for (const col of liveCollections) {
          const snap = await getDocs(collection(db, col)).catch(() => null);
          if (snap && !snap.empty) {
            liveSnap = snap;
            break;
          }
        }

        if (liveSnap && !liveSnap.empty) {
          const d = liveSnap.docs[0].data();
          const link = getMediaLink(d) || extractString(d.youtube_link || d.url || d.link, "");
          const dbThumb = getMediaImage(d);
          const ytThumb = getVideoThumbnail(link || d);
          const resolvedBg = dbThumb || ytThumb || "";

          if (isMounted) {
            setLiveInfo(prev => ({
              title: extractString(d.title || d.name || d.heading || d.caption, prev.title),
              schedule: extractString(d.schedule || d.day || d.date, prev.schedule),
              time: extractString(d.time || d.timing, prev.time),
              isLive: d.isLive !== undefined ? Boolean(d.isLive) : prev.isLive,
              bgImg: resolvedBg || prev.bgImg
            }));
          }
        }

        // 2. Fetch Facebook Videos from all possible collection names
        const fetched = [];
        const seenDocIds = new Set();
        const videoCollectionNames = ['fb-video', 'fb_video', 'facebook-video', 'fb-videos', 'fb_videos', 'facebook_video', 'facebook-videos', 'videos'];

        for (const colName of videoCollectionNames) {
          const snap = await getDocs(collection(db, colName)).catch(() => null);
          if (snap && !snap.empty) {
            snap.docs.forEach(doc => {
              if (seenDocIds.has(doc.id)) return;
              seenDocIds.add(doc.id);
              const d = doc.data();
              const link = getMediaLink(d);
              const title = extractString(d.title || d.name || d.caption || d.description || d.desc, "Diocesan Facebook Video");
              const description = extractString(d.description || d.desc || d.details || d.summary || d.subtitle || "");
              const customThumb = getMediaImage(d);
              const resolvedUrl = customThumb || getVideoThumbnail(link || d) || churchThumb;
              const order = extractOrder(d.order);

              if (link || resolvedUrl || title) {
                fetched.push({
                  id: doc.id,
                  ...d,
                  title,
                  description,
                  url: resolvedUrl,
                  thumbnail: customThumb || resolvedUrl,
                  videoLink: link,
                  type: 'video',
                  videoWidth: d.videoWidth || d.width || d.video_width || null,
                  videoHeight: d.videoHeight || d.height || d.video_height || null,
                  orientation: d.orientation || d.videoOrientation || d.video_orientation || d.aspectRatio || d.aspect_ratio || d.format || '',
                  order
                });
              }
            });
          }
        }

        // 3. Fetch Gallery Photos from 'gallery' / 'photo-gallery' / 'photos'
        const photoCollections = ['gallery', 'photo-gallery', 'photos'];
        for (const colName of photoCollections) {
          const galSnap = await getDocs(collection(db, colName)).catch(() => null);
          if (galSnap && !galSnap.empty) {
            galSnap.docs.forEach(doc => {
              if (seenDocIds.has(doc.id)) return;
              seenDocIds.add(doc.id);
              const d = doc.data();
              const image = getMediaImage(d);
              const title = extractString(d.title || d.name, "Diocesan Photo");
              const description = extractString(d.description || d.desc || d.details || d.summary || d.subtitle || "");
              const link = getMediaLink(d);
              const order = extractOrder(d.order);
              if (image) {
                fetched.push({
                  id: doc.id,
                  ...d,
                  title,
                  description,
                  url: image,
                  thumbnail: image,
                  videoLink: link,
                  type: link ? 'video' : 'photo',
                  videoWidth: d.videoWidth || d.width || d.video_width || null,
                  videoHeight: d.videoHeight || d.height || d.video_height || null,
                  orientation: d.orientation || d.aspectRatio || d.aspect_ratio || d.format || '',
                  order
                });
              }
            });
          }
        }

        if (isMounted) {
          fetched.sort((a, b) => {
            const orderA = a.order !== undefined ? a.order : 999;
            const orderB = b.order !== undefined ? b.order : 999;
            if (orderA !== orderB) return orderA - orderB;
            return getTimestampMs(b) - getTimestampMs(a);
          });

          const uniqueFetched = [];
          const seen = new Set();
          for (const item of fetched) {
            const key = item.videoLink || item.url || item.title;
            if (key && !seen.has(key)) {
              seen.add(key);
              uniqueFetched.push(item);
            }
          }

          setGalleryItems(uniqueFetched.slice(0, 3));
        }
      } catch (err) {
        console.error("Error fetching media row:", err);
      }
    }

    fetchMedia();
    return () => { isMounted = false; };
  }, []);

  const renderMediaItem = (item, defaultFallback) => {
    const activeItem = item || defaultFallback;
    if (!activeItem) return null;

    const handleClick = (e) => {
      e.stopPropagation();
      setModalOrientation(null);
      setSelectedVideo(activeItem);
    };

    const customDbImg = getMediaImage(activeItem);
    const videoThumb = getVideoThumbnail(activeItem);
    const thumbnailUrl = customDbImg || videoThumb || (typeof activeItem.url === 'string' && !activeItem.url.includes('facebook.com') ? activeItem.url : '') || churchThumb;

    // Guaranteed single clean title/description text
    const mainText = (activeItem.title && activeItem.title.trim())
      ? activeItem.title.trim()
      : (activeItem.description && activeItem.description.trim())
        ? activeItem.description.trim()
        : (defaultFallback?.title || "Diocesan Holy Mass & Prayer");

    return (
      <div
        onClick={handleClick}
        className="w-full h-full relative group cursor-pointer overflow-hidden bg-black rounded-[24px] flex items-center justify-center"
      >
        <img
          src={thumbnailUrl}
          alt=""
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = churchThumb;
          }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-90 group-hover:opacity-100"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent opacity-90 group-hover:opacity-95 transition-opacity duration-300"></div>

        {/* Play Button - EXACT DEAD CENTER OF CARD */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#842343]/90 text-white flex items-center justify-center pl-0.5 shadow-[0_4px_20px_rgba(0,0,0,0.3)] group-hover:scale-115 group-hover:bg-[#842343] transition-all duration-300 border-2 border-white/40 backdrop-blur-xs">
            <FaPlay className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-white" />
          </div>
        </div>

        {/* Facebook badge top right */}
        <span className="absolute top-2.5 right-2.5 bg-[#1877F2] text-white p-1.5 rounded-full text-[10px] shadow-md z-10 pointer-events-none">
          <FaFacebook className="w-3 h-3" />
        </span>

        {/* Single clean larger description overlay at bottom left */}
        <div className="absolute bottom-0 left-0 right-0 p-3.5 sm:p-4 text-white z-20 pointer-events-none bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-10 text-left">
          <p className="text-[14px] sm:text-[15px] font-serif font-bold leading-snug drop-shadow-md text-white group-hover:text-[#f0dfba] transition-colors line-clamp-2">
            {mainText}
          </p>
        </div>
      </div>
    );
  };

  return (
    <section className="py-1 sm:py-2 bg-transparent">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Live Streaming */}
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-3.5">
              <div className="flex items-center gap-2.5">
                <h2 className="font-serif font-bold text-[22px] text-[#842343]">
                  Live <span className="text-[#1a1715]">Stream</span>
                </h2>
                {liveInfo.isLive && (
                  <div className="bg-red-600 px-2.5 py-0.5 rounded-full text-[9px] font-extrabold tracking-[0.14em] flex items-center gap-1.5 shadow-[0_0_12px_rgba(220,38,38,0.5)] text-white">
                    <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>
                    LIVE
                  </div>
                )}
              </div>
            </div>

            <div
              className="bg-gradient-to-br from-[#4A1525] via-[#2A0D18] to-[#12050B] rounded-[24px] relative overflow-hidden h-[260px] sm:h-[280px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] group cursor-pointer hover:shadow-[0_12px_40px_rgba(0,0,0,0.2)] transition-all duration-500"
              onClick={() => onSelectSection('live')}
            >
              <img
                src={liveInfo.bgImg || churchThumb}
                alt=""
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = churchThumb;
                }}
                className="absolute inset-0 w-full h-full object-cover filter brightness-[0.5] group-hover:scale-105 group-hover:brightness-[0.6] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/50"></div>

              <div className="relative z-10 p-6 sm:p-7 flex flex-col h-full text-white justify-between">
                <div>
                  <h3 className="font-serif font-bold text-[20px] sm:text-[22px] leading-tight mb-1 text-[#f0dfba] drop-shadow-md">
                    {liveInfo.title}
                  </h3>
                  <p className="text-[13px] sm:text-[14px] font-medium text-white/90 drop-shadow-sm">
                    {liveInfo.schedule}
                  </p>
                  <p className="text-[17px] sm:text-[18px] font-bold text-white drop-shadow-sm">
                    {liveInfo.time}
                  </p>
                </div>

                <button
                  onClick={() => onSelectSection('live')}
                  className="bg-[#842343] hover:bg-[#6c1b35] text-white font-sans font-semibold text-[13px] py-2.5 px-5 rounded-full w-max shadow-[0_6px_16px_rgba(132,35,67,0.35)] group-hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-2 cursor-pointer"
                >
                  <span>Watch Live</span>
                  <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[8px] pl-[1px]">▶</span>
                </button>
              </div>
            </div>
          </div>

          {/* Moments of Faith */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div className="flex justify-between items-start mb-2.5 cursor-pointer group" onClick={() => onSelectSection('gallery')}>
              <div>
                <h2 className="font-serif font-bold text-[22px] text-[#842343] leading-none">
                  Moments of <span className="text-[#1a1715]">Faith</span>
                </h2>
                <p className="text-[12px] text-[#8A7E79] font-sans mt-1">
                  Holy services, choir ministries and parish celebrations across Malabar.
                </p>
              </div>
              <button
                onClick={() => onSelectSection('gallery')}
                className="text-[#842343] text-xs font-semibold uppercase tracking-wide flex items-center gap-1 group cursor-pointer shrink-0 mt-0.5"
              >
                <span className="group-hover:underline underline-offset-4 decoration-2 decoration-[#c39b5b]">View Gallery</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
            </div>

            <div className="flex sm:grid sm:grid-cols-3 gap-3.5 sm:gap-4 overflow-x-auto no-scrollbar scroll-smooth snap-x pb-1 sm:pb-0 h-[260px] sm:h-[280px]">
              {/* Tile 1 */}
              <div className="w-[84%] sm:w-auto shrink-0 snap-center sm:col-span-1 rounded-[24px] overflow-hidden relative group shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-gray-100 bg-[#FAF6F2] h-full">
                {renderMediaItem(galleryItems[0], DEFAULT_MOMENTS[0])}
              </div>

              {/* Tile 2 */}
              <div className="w-[84%] sm:w-auto shrink-0 snap-center sm:col-span-1 rounded-[24px] overflow-hidden relative group shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-gray-100 bg-[#FAF6F2] h-full">
                {renderMediaItem(galleryItems[1], DEFAULT_MOMENTS[1])}
              </div>

              {/* Tile 3 */}
              <div className="w-[84%] sm:w-auto shrink-0 snap-center sm:col-span-1 rounded-[24px] overflow-hidden relative group shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-gray-100 bg-[#FAF6F2] h-full">
                {renderMediaItem(galleryItems[2], DEFAULT_MOMENTS[2])}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Video Modal with Dynamic Aspect Ratio */}
      {selectedVideo && (() => {
        const videoUrl = selectedVideo.videoLink || selectedVideo.link || (typeof selectedVideo.url === 'string' ? selectedVideo.url : '');
        const currentOrientation = modalOrientation || (getVideoOrientation(selectedVideo) === 'portrait' ? 'portrait' : 'landscape');

        const isPortrait = currentOrientation === 'portrait';
        const modalStyle = {
          aspectRatio: isPortrait ? '9 / 16' : '16 / 9',
          maxWidth: isPortrait ? '420px' : '920px',
          width: '92%',
          maxHeight: isPortrait ? '85vh' : '82vh',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        };

        const activeItemWithOrientation = {
          ...selectedVideo,
          orientation: isPortrait ? 'portrait' : 'landscape'
        };

        return (
          <div
            className="fixed inset-0 bg-black/85 backdrop-blur-md z-[100] flex items-center justify-center p-3 sm:p-6 w-screen h-screen"
            onClick={() => setSelectedVideo(null)}
          >
            <div
              style={modalStyle}
              className="bg-[#1C1622] rounded-2xl overflow-hidden shadow-2xl relative border border-white/15 text-white flex flex-col my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <div className="absolute top-3 right-3 z-30 flex items-center">
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center text-white transition-colors cursor-pointer backdrop-blur-md border border-white/20 shadow-md"
                  title="Close Video"
                >
                  <LuX className="w-4 h-4" />
                </button>
              </div>

              <div className="relative w-full flex-1 bg-black flex items-center justify-center overflow-hidden">
                {videoUrl ? (
                  <VideoPlayer
                    key={selectedVideo.id || videoUrl}
                    item={activeItemWithOrientation}
                    url={videoUrl}
                    title={selectedVideo.title}
                    autoplay={true}
                  />
                ) : (
                  <img
                    src={selectedVideo.url}
                    alt={selectedVideo.title}
                    className="w-full h-full object-contain"
                    onLoad={(e) => {
                      const { naturalWidth, naturalHeight } = e.currentTarget;
                      if (naturalWidth && naturalHeight) {
                        setModalOrientation(naturalHeight > naturalWidth ? 'portrait' : 'landscape');
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </section>
  );
}
